import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Calendar, Clock } from 'lucide-react';
import { Report, Customer } from '@/types';
import { useTranslation } from '@/contexts/TranslationContext';
import { useAppContext } from '@/contexts/AppContext';
import ExportReports from './ExportReports';

interface ReportTableWithFiltersProps {
  reports: Report[];
  customers: Customer[];
  onEdit: (report: Report) => void;
  onDelete: (id: string) => void;
}


const ReportTableWithFilters: React.FC<ReportTableWithFiltersProps> = ({ reports, customers, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();
  const [businessFilter, setBusinessFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [salespersonFilter, setSalespersonFilter] = useState('');

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.businessName || 'Unknown';
  };

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const businessName = getCustomerName(report.customerId).toLowerCase();
      const matchBusiness = businessName.includes(businessFilter.toLowerCase());
      const matchDate = !dateFilter || report.date?.includes(dateFilter);
      const matchSalesperson = !salespersonFilter || report.salespersonName?.toLowerCase().includes(salespersonFilter.toLowerCase());
      return matchBusiness && matchDate && matchSalesperson;
    });
  }, [reports, businessFilter, dateFilter, salespersonFilter, customers]);


  const formatProducts = (product: string | string[]) => {
    if (Array.isArray(product)) return product.join(', ');
    return product;
  };

  return (
    <div>
      <div className="flex gap-4 mb-4 flex-wrap">
        <div className="flex-1 min-w-[200px]"><Input placeholder="Filter by business name" value={businessFilter} onChange={(e) => setBusinessFilter(e.target.value)} /></div>
        <div className="flex-1 min-w-[200px]"><Input type="date" placeholder="Filter by date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} /></div>
        {currentUser?.role === 'admin' && <div className="flex-1 min-w-[200px]"><Input placeholder="Filter by salesperson" value={salespersonFilter} onChange={(e) => setSalespersonFilter(e.target.value)} /></div>}
        <ExportReports reports={reports} customers={customers} />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('businessName')}</TableHead>
              <TableHead>Business Type</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>{t('director')}</TableHead>
              <TableHead>{t('product')}</TableHead>
              {currentUser?.role === 'admin' && <TableHead>Salesperson</TableHead>}
              <TableHead>{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredReports.length === 0 ? (
              <TableRow><TableCell colSpan={currentUser?.role === 'admin' ? 7 : 6} className="text-center text-muted-foreground">{t('noData')}</TableCell></TableRow>
            ) : (
              filteredReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell className="font-medium">{getCustomerName(report.customerId)}</TableCell>
                  <TableCell><Badge variant="outline">{report.businessType}</Badge></TableCell>
                  <TableCell>
                    <div className="flex flex-col text-sm">
                      <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}</div>
                      <div className="flex items-center gap-1 text-gray-500"><Clock className="h-3 w-3" />{report.time || 'N/A'}</div>
                    </div>
                  </TableCell>
                  <TableCell>{report.director}</TableCell>
                  <TableCell className="max-w-xs truncate">{formatProducts(report.product)}</TableCell>
                  {currentUser?.role === 'admin' && <TableCell><Badge>{report.salespersonName || 'N/A'}</Badge></TableCell>}

                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => onEdit(report)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => onDelete(report.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ReportTableWithFilters;
